import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Dialog, InputBase, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import EastRoundedIcon from "@mui/icons-material/EastRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { isEmailed, isListed, isUnlocked } from "../data/resources";

// Search across the whole Trust Center (⌘K / Ctrl+K, or the bar's Search).
// Everything is already on the page, so it searches in the browser: no
// request, instant results. ↑ ↓ move, Enter opens, Esc closes.

const PER_GROUP = 6;

const words = (text) =>
  String(text || "")
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);

// Every word typed must appear somewhere in the item.
const matches = (item, query) => {
  const terms = words(query);
  if (terms.length === 0) return true;
  const haystack =
    `${item.title} ${item.subtitle || ""} ${item.keywords || ""}`.toLowerCase();
  return terms.every((term) => haystack.includes(term));
};

const buildItems = (data, { access, tabs }) => {
  const categories = new Map(
    (data.resourceCategories || []).map((c) => [c.id, c.name])
  );
  const items = [];
  (data.resources || []).filter(isListed).forEach((r) => {
    const locked = !isUnlocked(r, access);
    items.push({
      key: `resource-${r.id}`,
      group: "Documents",
      icon: r.type === "link" ? LinkOutlinedIcon : DescriptionOutlinedIcon,
      title: r.name,
      subtitle: categories.get(r.categoryId),
      keywords: r.description,
      hint: isEmailed(r)
        ? "Email me"
        : locked
          ? "Request"
          : r.type === "link"
            ? "Open link"
            : "View",
      locked,
      action: { type: "resource", resource: r },
    });
  });
  (data.frameworks || []).forEach((f) =>
    items.push({
      key: `framework-${f.id}`,
      group: "Compliance",
      icon: ShieldOutlinedIcon,
      title: f.name,
      subtitle: f.status === "compliant" ? "Compliant" : "In progress",
      // Its report, if it has one; else the Overview, which lists frameworks.
      action: (f.resourceIds || []).length
        ? { type: "report", resourceId: f.resourceIds[0] }
        : { type: "tab", tab: "overview" },
    })
  );
  (data.controls || []).forEach((c) =>
    items.push({
      key: `control-${c.id}`,
      group: "Controls",
      icon: FactCheckOutlinedIcon,
      title: c.title,
      subtitle: c.domain,
      keywords: `${c.name} ${c.description || ""}`,
      hint:
        c.status === "pass"
          ? "Passing"
          : c.status === "fail"
            ? "Failing"
            : undefined,
      action: { type: "control", control: c },
    })
  );
  (data.subprocessors || []).forEach((s) =>
    items.push({
      key: `subprocessor-${s.id}`,
      group: "Subprocessors",
      icon: BusinessOutlinedIcon,
      title: s.name,
      subtitle: s.purpose,
      keywords: (s.dataLocations || []).join(" "),
      action: { type: "subprocessor", id: s.id },
    })
  );
  (data.faqs || []).forEach((f) =>
    items.push({
      key: `faq-${f.id}`,
      group: "FAQ",
      icon: HelpOutlineRoundedIcon,
      title: f.question,
      keywords: f.answer,
      action: { type: "tab", tab: "faq" },
    })
  );
  (data.updates || []).forEach((u) =>
    items.push({
      key: `update-${u.id}`,
      group: "Updates",
      icon: CampaignOutlinedIcon,
      title: u.title,
      subtitle: u.category,
      keywords: u.body,
      action: { type: "update", id: u.id },
    })
  );
  const jump = tabs.map((tab) => ({
    key: `tab-${tab.id}`,
    group: "Go to",
    icon: EastRoundedIcon,
    title: tab.label,
    action: { type: "tab", tab: tab.id },
  }));
  return { items, jump };
};

const GROUP_ORDER = [
  "Documents",
  "Compliance",
  "Controls",
  "Subprocessors",
  "FAQ",
  "Updates",
  "Go to",
];

const SearchPalette = ({ open, onClose, data, access, tabs, onSelect }) => {
  const theme = useTheme();
  const t = theme.trust;
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  // Groups opened past the first PER_GROUP results with "Show all".
  const [expanded, setExpanded] = useState(() => new Set());
  const listRef = useRef(null);
  const { items, jump } = useMemo(
    () => buildItems(data, { access, tabs }),
    [data, access, tabs]
  );

  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
    }
  }, [open]);

  // Results in display order, grouped. With nothing typed: a few documents
  // and the sections to jump to. A group with more matches than PER_GROUP
  // ends in a "Show all" row, so no match is out of reach.
  const groups = useMemo(() => {
    const pool = query.trim()
      ? [...items, ...jump].filter((item) => matches(item, query))
      : [...items.filter((i) => i.group === "Documents").slice(0, 5), ...jump];
    const byGroup = new Map();
    pool.forEach((item) => {
      byGroup.set(item.group, [...(byGroup.get(item.group) || []), item]);
    });
    return GROUP_ORDER.filter((g) => byGroup.has(g)).map((g) => {
      const all = byGroup.get(g);
      if (expanded.has(g) || all.length <= PER_GROUP)
        return { name: g, items: all };
      return {
        name: g,
        items: [
          ...all.slice(0, PER_GROUP),
          {
            key: `more-${g}`,
            group: g,
            icon: ExpandMoreRoundedIcon,
            title: `Show all ${all.length} in ${g}`,
            action: { type: "expand", group: g },
          },
        ],
      };
    });
  }, [items, jump, query, expanded]);
  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  useEffect(() => {
    setActive(0);
    setExpanded(new Set());
  }, [query]);
  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-index="${active}"]`)
      ?.scrollIntoView?.({ block: "nearest" });
  }, [active]);

  const choose = (item) => {
    if (!item) return;
    // "Show all" opens the group in place; the highlight stays on the row
    // that took its spot.
    if (item.action.type === "expand") {
      setExpanded((prev) => new Set(prev).add(item.action.group));
      return;
    }
    onClose();
    onSelect(item.action);
  };

  const onKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((i) => Math.min(flat.length - 1, i + 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (event.key === "Enter") {
      event.preventDefault();
      choose(flat[active]);
    }
  };

  let index = -1;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-label="Search this Trust Center"
      PaperProps={{
        sx: {
          alignSelf: "flex-start",
          mt: { xs: 2, sm: "12vh" },
          borderRadius: "16px",
          border: `1px solid ${t.line}`,
          boxShadow: "0 24px 64px -24px rgba(18, 26, 58, 0.45)",
          overflow: "hidden",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(18, 26, 58, 0.35)",
            backdropFilter: "blur(2px)",
          },
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${t.line}` }}
      >
        <SearchRoundedIcon aria-hidden="true" sx={{ color: t.muted }} />
        <InputBase
          autoFocus
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search documents, controls, vendors…"
          inputProps={{
            "aria-label": "Search",
            role: "combobox",
            "aria-expanded": true,
            "aria-controls": "trust-search-results",
            "aria-activedescendant": flat[active]
              ? `trust-search-${flat[active].key}`
              : undefined,
            spellCheck: false,
            autoComplete: "off",
          }}
          sx={{ fontSize: 16, color: t.ink }}
        />
        <Box
          component="kbd"
          sx={{
            fontFamily: "inherit",
            fontSize: 11.5,
            color: t.muted,
            border: `1px solid ${t.line}`,
            borderRadius: "6px",
            px: 0.75,
            py: 0.25,
          }}
        >
          Esc
        </Box>
      </Stack>

      <Box
        ref={listRef}
        id="trust-search-results"
        role="listbox"
        aria-label="Search results"
        sx={{ maxHeight: { xs: "60vh", sm: 420 }, overflowY: "auto", py: 1 }}
      >
        {flat.length === 0 ? (
          <Box sx={{ px: 2.5, py: 4, textAlign: "center" }}>
            <Typography sx={{ fontWeight: 500, color: t.ink }}>
              No results for “{query.trim()}”
            </Typography>
            <Typography variant="body2" sx={{ color: t.muted, mt: 0.5 }}>
              Try a document name, a control, or a vendor.
            </Typography>
          </Box>
        ) : (
          groups.map((group) => (
            <Box
              key={group.name}
              role="group"
              aria-label={group.name}
              sx={{ mb: 0.5 }}
            >
              <Typography
                sx={{
                  px: 2.5,
                  pt: 1,
                  pb: 0.5,
                  fontSize: 12,
                  fontWeight: 500,
                  color: t.muted,
                }}
              >
                {group.name}
              </Typography>
              {group.items.map((item) => {
                index += 1;
                const i = index;
                const isActive = i === active;
                const Icon = item.icon;
                return (
                  <Stack
                    key={item.key}
                    id={`trust-search-${item.key}`}
                    data-index={i}
                    role="option"
                    aria-selected={isActive}
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    onMouseMove={() => setActive(i)}
                    onClick={() => choose(item)}
                    sx={{
                      mx: 1,
                      px: 1.5,
                      py: 1,
                      borderRadius: "10px",
                      cursor: "pointer",
                      backgroundColor: isActive
                        ? t.secondarySoft
                        : "transparent",
                    }}
                  >
                    <Icon
                      aria-hidden="true"
                      sx={{
                        fontSize: 18,
                        color: isActive ? t.secondary : t.muted,
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        noWrap
                        sx={{ fontSize: 14, fontWeight: 500, color: t.inkName }}
                      >
                        {item.title}
                      </Typography>
                      {item.subtitle && (
                        <Typography
                          noWrap
                          sx={{ fontSize: 12.5, color: t.muted }}
                        >
                          {item.subtitle}
                        </Typography>
                      )}
                    </Box>
                    {item.hint && (
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        sx={{
                          flexShrink: 0,
                          color:
                            item.hint === "Failing" ? t.attention : t.muted,
                        }}
                      >
                        {item.locked && (
                          <LockOutlinedIcon
                            aria-hidden="true"
                            sx={{ fontSize: 14 }}
                          />
                        )}
                        <Typography sx={{ fontSize: 12.5, fontWeight: 500 }}>
                          {item.hint}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                );
              })}
            </Box>
          ))
        )}
      </Box>

      <Stack
        direction="row"
        spacing={2}
        sx={{
          px: 2.5,
          py: 1.25,
          borderTop: `1px solid ${t.line}`,
          color: t.muted,
          fontSize: 12,
          display: { xs: "none", sm: "flex" },
        }}
      >
        <span>↑ ↓ to move</span>
        <span>Enter to open</span>
        <span>Esc to close</span>
      </Stack>
    </Dialog>
  );
};

export default SearchPalette;
